import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppHeader from "./components/AppHeader";
import LevelPage from "./components/LevelPage";
import ReviewPage from "./components/ReviewPage";
import { ProgressWatcher, RankChip, Toasts } from "./components/Rpg";
import TrackPage from "./components/TrackPage";
import { MenuIcon } from "./components/Icons";
import { findLevel, getTrack, tracks } from "./lib/content";
import { hrefFor, useRoute } from "./lib/router";
import { useTheme } from "./lib/theme";

function NotFound({ message }) {
  const { t } = useTranslation();
  return (
    <main id="main" className="page empty-state">
      <h1>{message}</h1>
      <a className="button button--primary" href={hrefFor(null)}>
        {t("track.backHome")}
      </a>
    </main>
  );
}

function App() {
  const { t } = useTranslation();
  const { theme, toggle } = useTheme();
  const { trackId, levelId } = useRoute();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // With a single track there is nothing to choose, so the home page is that track.
  const track = trackId ? getTrack(trackId) : tracks[0];
  const isReview = levelId === "review";
  const located = track && levelId && !isReview ? findLevel(track, levelId) : null;

  useEffect(() => {
    setSidebarOpen(false);
    window.scrollTo?.(0, 0);
  }, [trackId, levelId]);

  useEffect(() => {
    document.title = located ? `${located.level.title} · Code Forge` : isReview ? `${t("review.title")} · Code Forge` : "Code Forge";
  }, [located, isReview, t]);

  const crumbs = [];
  if (track) crumbs.push({ label: track.title, href: hrefFor(track.id) });
  if (located) crumbs.push({ label: located.chapter.title, href: hrefFor(track.id) });
  if (track && isReview) crumbs.push({ label: t("review.title") });

  let content;
  if (!track) content = <NotFound message={t("track.notFound")} />;
  else if (isReview) content = <ReviewPage key={`review-${levelId}`} track={track} theme={theme} />;
  else if (levelId && !located) content = <NotFound message={t("level.notFound")} />;
  else if (located)
    content = (
      <LevelPage
        key={located.level.id}
        track={track}
        located={located}
        theme={theme}
        sidebarOpen={sidebarOpen}
        onCloseSidebar={() => setSidebarOpen(false)}
      />
    );
  else content = <TrackPage track={track} />;

  return (
    <div className={`app ${located ? "app--workspace" : ""}`}>
      {track && <ProgressWatcher track={track} />}
      <Toasts />
      <AppHeader theme={theme} onToggleTheme={toggle} crumbs={crumbs} rankChip={track && <RankChip track={track} />}>
        {located && (
          <button
            type="button"
            className="icon-button sidebar-toggle"
            onClick={() => setSidebarOpen((open) => !open)}
            aria-expanded={sidebarOpen}
            aria-controls="level-sidebar"
            aria-label={t("level.openList")}
            title={t("level.openList")}
          >
            <MenuIcon />
          </button>
        )}
      </AppHeader>
      {content}
    </div>
  );
}

export default App;
